import { Check, Plus } from 'lucide-react';
import { LayoutGroup, motion } from 'motion/react';
import { useId, useMemo, useState, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';

import styles from './MultiPicker.module.scss';

export interface MultiPickerItem {
  value: string;
  label: string;
  description?: string | undefined;
  leading?: ReactNode;
}

export interface MultiPickerProps {
  items: MultiPickerItem[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Кнопка, открывающая список. По умолчанию — «+» с подписью. */
  trigger?: ReactNode;
  label: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /**
   * Создать новый пункт из набранного текста — для тегов. Возвращает
   * значение созданного, и оно сразу добавляется к выбранным.
   */
  onCreate?: ((name: string) => Promise<string>) | undefined;
  createLabel?: string;
  disabled?: boolean;
  /**
   * Один из списка: выбор заменяет прежний и закрывает список. Тот же вид,
   * что у множественного — ревьюер и исполнители выглядят одинаково.
   */
  single?: boolean;
  /** Открыть список сразу при появлении — для только что добавленного фильтра. */
  defaultOpen?: boolean;
}

/**
 * Выбор нескольких из списка с поиском: исполнители, теги.
 *
 * Список остаётся открытым после клика — обычно выбирают сразу
 * нескольких. Отмеченные — галочкой; подложка одна, плывёт за курсором и
 * стрелками, как везде.
 */
export function MultiPicker({
  items,
  selected,
  onChange,
  trigger,
  label,
  searchPlaceholder = 'Поиск…',
  emptyText = 'Ничего не найдено',
  onCreate,
  createLabel = 'Создать',
  single = false,
  defaultOpen = false,
  disabled,
}: MultiPickerProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [activeRaw, setActive] = useState(0);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(defaultOpen);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      items.filter(
        (i) =>
          q === '' ||
          i.label.toLowerCase().includes(q) ||
          (i.description?.toLowerCase().includes(q) ?? false),
      ),
    [items, q],
  );

  const canCreate =
    onCreate !== undefined && q !== '' && !items.some((i) => i.label.toLowerCase() === q);
  const rows = filtered.length + (canCreate ? 1 : 0);
  const active = Math.min(activeRaw, Math.max(rows - 1, 0));

  const toggle = (value: string) => {
    if (single) {
      onChange(selected.includes(value) ? [] : [value]);
      setOpen(false);

      return;
    }

    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const create = async () => {
    if (!onCreate || creating) {
      return;
    }

    setCreating(true);

    try {
      const value = await onCreate(query.trim());

      onChange([...selected, value]);
      setQuery('');
    } catch {
      // Об ошибке сообщает тот, кто создаёт (тостом); список остаётся
      // открытым с набранным текстом — можно поправить и повторить.
    } finally {
      setCreating(false);
    }
  };

  const activate = (index: number) => {
    if (canCreate && index === filtered.length) {
      void create();

      return;
    }

    const item = filtered[index];

    if (item) {
      toggle(item.value);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        setOpen(open);

        if (!open) {
          setQuery('');
          setActive(0);
        }
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ?? (
          <button type="button" className={styles['trigger']} aria-label={label}>
            <Plus />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className={styles['panel']} align="start">
        <input
          className={styles['search']}
          value={query}
          placeholder={searchPlaceholder}
          autoFocus
          aria-label={label}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActive((i) => Math.min(i + 1, rows - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              activate(active);
            }
          }}
        />
        <LayoutGroup id={listId}>
          <ul className={styles['list']} role="listbox" aria-multiselectable="true">
            {rows === 0 && <li className={styles['empty']}>{emptyText}</li>}
            {filtered.map((item, index) => {
              const checked = selected.includes(item.value);

              return (
                <li
                  key={item.value}
                  role="option"
                  aria-selected={checked}
                  className={styles['item']}
                  onPointerMove={() => {
                    setActive(index);
                  }}
                  onClick={() => {
                    toggle(item.value);
                  }}
                >
                  {index === active && (
                    <motion.span
                      className={styles['item__highlight']}
                      layoutId={`${listId}-hl`}
                      transition={springs.rubber}
                      aria-hidden="true"
                    />
                  )}
                  {item.leading !== undefined && (
                    <span className={styles['item__leading']}>{item.leading}</span>
                  )}
                  <span className={styles['item__text']}>
                    <span className={styles['item__label']}>{item.label}</span>
                    {item.description !== undefined && (
                      <span className={styles['item__description']}>{item.description}</span>
                    )}
                  </span>
                  <span className={cx(styles['item__check'], checked && styles['item__check--on'])}>
                    <Check />
                  </span>
                </li>
              );
            })}
            {canCreate && (
              <li
                role="option"
                aria-selected={false}
                className={cx(styles['item'], styles['item--create'])}
                onPointerMove={() => {
                  setActive(filtered.length);
                }}
                onClick={() => {
                  void create();
                }}
              >
                {active === filtered.length && (
                  <motion.span
                    className={styles['item__highlight']}
                    layoutId={`${listId}-hl`}
                    transition={springs.rubber}
                    aria-hidden="true"
                  />
                )}
                <span className={styles['item__leading']}>
                  <Plus />
                </span>
                <span className={styles['item__text']}>
                  <span className={styles['item__label']}>
                    {createLabel} «{query.trim()}»
                  </span>
                </span>
              </li>
            )}
          </ul>
        </LayoutGroup>
      </PopoverContent>
    </Popover>
  );
}
